import { Suspense, useLayoutEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { PerspectiveCamera } from "@react-three/drei";

/**
 * Camera rig with an explicit, guaranteed look-at target. Rendering our own
 * `PerspectiveCamera` and pointing it ourselves removes any ambiguity around
 * R3F's default-camera behavior.
 */
function CameraRig({ position, fov = 45, target = [0, 0, 0] }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    ref.current?.lookAt(...target);
  });

  return <PerspectiveCamera makeDefault ref={ref} position={position} fov={fov} />;
}

/**
 * Reusable Canvas wrapper with sensible defaults (DPR clamp, antialiasing,
 * base lighting). Transparent by default (`alpha: true`, no scene
 * background ever set) so 3D content composites directly onto the page
 * instead of sitting inside an opaque rectangle. Page-specific 3D content
 * goes in `children`.
 */
export function Scene({ children, className, camera }) {
  return (
    <Canvas className={className} dpr={[1, 2]} gl={{ antialias: true, alpha: true }}>
      {camera && <CameraRig {...camera} />}
      <Suspense fallback={null}>
        <ambientLight intensity={0.5} />
        <directionalLight position={[4, 4, 4]} intensity={0.9} />
        {children}
      </Suspense>
    </Canvas>
  );
}
